import { RenderManager } from '../../../core/RenderManager';

export class GoalManager {
  private goals: boolean[] = []; // true = filled, false = open
  private gridCols: number;
  private renderManager: RenderManager;
  private readonly GOAL_COUNT: number = 5;
  
  // Animation properties
  private animationTimer: number = 0;
  
  constructor(gridCols: number, renderManager: RenderManager) {
    this.gridCols = gridCols;
    this.renderManager = renderManager;
    this.reset();
  }

  public reset(): void {
    // Initialize all goals as open (false)
    this.goals = new Array(this.GOAL_COUNT).fill(false);
    this.animationTimer = 0;
  }

  public isGoalOpen(goalIndex: number): boolean {
    if (goalIndex < 0 || goalIndex >= this.GOAL_COUNT) {
      return false;
    }
    return !this.goals[goalIndex];
  }

  public fillGoal(goalIndex: number): boolean {
    if (goalIndex < 0 || goalIndex >= this.GOAL_COUNT) {
      return false;
    }
    
    if (this.goals[goalIndex]) {
      return false; // Already filled
    }
    
    this.goals[goalIndex] = true;
    return true;
  }

  public allGoalsFilled(): boolean {
    return this.goals.every(goal => goal);
  }

  public getFilledGoalCount(): number {
    return this.goals.filter(goal => goal).length;
  }

  public getGoalPosition(goalIndex: number, gameWidth: number): { x: number, width: number } {
    if (goalIndex < 0 || goalIndex >= this.GOAL_COUNT) {
      return { x: 0, width: 0 };
    }
    
    const goalWidth = gameWidth / this.GOAL_COUNT;
    const x = goalIndex * goalWidth;
    
    return { x, width: goalWidth };
  }

  public getGoalIndexFromPosition(x: number, gameWidth: number): number {
    const goalWidth = gameWidth / this.GOAL_COUNT;
    const goalIndex = Math.floor(x / goalWidth);
    
    // Clamp to valid range
    return Math.max(0, Math.min(this.GOAL_COUNT - 1, goalIndex));
  }

  public update(deltaTime: number): void {
    this.animationTimer += deltaTime;
  }

  public render(offsetX: number, offsetY: number): void {
    const gameWidth = 480; // From RiverCrossingGame
    const goalRowY = offsetY; // Goals are at row 0
    const gridSize = 32;
    
    for (let i = 0; i < this.GOAL_COUNT; i++) {
      const goalPos = this.getGoalPosition(i, gameWidth);
      const goalX = offsetX + goalPos.x;
      const goalY = goalRowY;
      
      this.renderLilyPad(goalX, goalY, goalPos.width, gridSize, i);
    }
  }

  private renderLilyPad(x: number, y: number, width: number, height: number, goalIndex: number): void {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const padRadius = Math.min(width, height) * 0.4;
    
    if (this.goals[goalIndex]) {
      // Filled goal - lily pad with frog
      this.renderFilledLilyPad(centerX, centerY, padRadius);
    } else {
      // Open goal - empty lily pad
      this.renderEmptyLilyPad(centerX, centerY, padRadius);
    }
  }

  private renderEmptyLilyPad(centerX: number, centerY: number, radius: number): void {
    // Lily pad (green circle)
    this.renderManager.drawEllipse(
      centerX - radius,
      centerY - radius,
      radius,
      radius,
      '#228B22', // Forest green
      true
    );
    
    // Lily pad notch (darker green triangle using polygon)
    const notchSize = radius * 0.3;
    const notchPoints = [
      { x: centerX + radius * 0.7, y: centerY - notchSize },
      { x: centerX + radius * 0.7, y: centerY + notchSize },
      { x: centerX + radius, y: centerY }
    ];
    this.renderManager.drawPolygon(notchPoints, '#006400', true);
    
    // Lily pad outline
    this.renderManager.drawEllipse(
      centerX - radius,
      centerY - radius,
      radius,
      radius,
      '#006400', // Dark green
      false,
      1
    );
    
    // Water ripples (animated)
    const ripplePhase = (this.animationTimer * 2 + centerX * 0.1) % (Math.PI * 2);
    const rippleRadius = radius + Math.sin(ripplePhase) * 4;
    
    this.renderManager.drawEllipse(
      centerX - rippleRadius,
      centerY - rippleRadius,
      rippleRadius,
      rippleRadius,
      'rgba(0, 102, 204, 0.3)', // Semi-transparent blue
      false,
      1
    );
  }

  private renderFilledLilyPad(centerX: number, centerY: number, radius: number): void {
    // Lily pad base
    this.renderEmptyLilyPad(centerX, centerY, radius);
    
    // Frog on lily pad (smaller than normal frog)
    const frogSize = radius * 0.8;
    this.renderGoalFrog(centerX, centerY, frogSize);
    
    // Success glow effect
    const glowPhase = this.animationTimer * 3;
    const glowAlpha = (Math.sin(glowPhase) * 0.3 + 0.7) * 0.5;
    
    this.renderManager.drawEllipse(
      centerX - radius * 1.2,
      centerY - radius * 1.2,
      radius * 1.2,
      radius * 1.2,
      `rgba(255, 255, 0, ${glowAlpha})`, // Golden glow
      false,
      2
    );
  }

  private renderGoalFrog(centerX: number, centerY: number, size: number): void {
    // Mini frog body
    const bodyColor = '#00FF00';
    
    // Main body
    this.renderManager.drawEllipse(
      centerX - size * 0.3,
      centerY - size * 0.2,
      size * 0.3,
      size * 0.2,
      bodyColor,
      true
    );
    
    // Eyes
    const eyeSize = size * 0.05;
    this.renderManager.drawEllipse(
      centerX - size * 0.15,
      centerY - size * 0.25,
      eyeSize,
      eyeSize,
      '#FFFF00',
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.05,
      centerY - size * 0.25,
      eyeSize,
      eyeSize,
      '#FFFF00',
      true
    );
    
    // Legs (simple dots)
    const legSize = size * 0.04;
    
    // Front legs
    this.renderManager.drawEllipse(
      centerX - size * 0.25,
      centerY - size * 0.1,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.15,
      centerY - size * 0.1,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    // Back legs
    this.renderManager.drawEllipse(
      centerX - size * 0.25,
      centerY + size * 0.05,
      legSize,
      legSize,
      bodyColor,
      true
    );
    
    this.renderManager.drawEllipse(
      centerX + size * 0.15,
      centerY + size * 0.05,
      legSize,
      legSize,
      bodyColor,
      true
    );
  }

  public getGoalStats(): { total: number, filled: number, remaining: number } {
    const filled = this.getFilledGoalCount();
    return {
      total: this.GOAL_COUNT,
      filled: filled,
      remaining: this.GOAL_COUNT - filled
    };
  }

  // Check if a specific position is within a goal area
  public isPositionInGoal(x: number, gameWidth: number): boolean {
    const goalIndex = this.getGoalIndexFromPosition(x, gameWidth);
    const goalPos = this.getGoalPosition(goalIndex, gameWidth);
    
    // Check if x is within the goal boundaries with some tolerance
    const tolerance = goalPos.width * 0.1; // 10% tolerance
    return x >= goalPos.x - tolerance && x <= goalPos.x + goalPos.width + tolerance;
  }

  // Get completion percentage for progress tracking
  public getCompletionPercentage(): number {
    return (this.getFilledGoalCount() / this.GOAL_COUNT) * 100;
  }
}